import { getErrorCode, getErrorMessage } from '@/utils/errorUtils'
import createLogger from '@/utils/logger'

const logger = createLogger('components/consultation/hub/consultationsHubFetch.ts')

export const CONSULTATIONS_HUB_FETCH_MAX_ATTEMPTS = 3
export const CONSULTATIONS_HUB_FETCH_RETRY_DELAYS_MS = [500, 1500] as const

const TRANSIENT_HUB_FETCH_ERROR_CODES = new Set([
  'functions/internal',
  'functions/unavailable',
  'functions/deadline-exceeded',
  'functions/cancelled',
  'functions/resource-exhausted',
  'internal',
  'unavailable',
  'deadline-exceeded',
  'cancelled',
  'resource-exhausted',
])

export type ConsultationsHubFetchPhase = 'initial' | 'refresh'

export function isTransientConsultationsHubFetchError(error: unknown): boolean {
  const code = getErrorCode(error)
  return code != null && TRANSIENT_HUB_FETCH_ERROR_CODES.has(code)
}

type CallableUserErrorMessages = {
  fallback: string
  internal: string
  deadlineExceeded: string
  unavailable: string
  unauthenticated: string
}

export function formatCallableUserError(
  error: unknown,
  messages: CallableUserErrorMessages,
): string {
  const code = getErrorCode(error)
  const message = getErrorMessage(error)

  if (code === 'functions/internal' || message === 'internal') {
    return messages.internal
  }
  if (code === 'functions/deadline-exceeded') {
    return messages.deadlineExceeded
  }
  if (code === 'functions/unavailable' || code === 'functions/resource-exhausted') {
    return messages.unavailable
  }
  if (code === 'functions/unauthenticated') {
    return messages.unauthenticated
  }
  if (message && message !== 'internal' && !message.startsWith('functions/')) {
    return message
  }
  return messages.fallback
}

export function formatConsultationsHubFetchError(error: unknown): string {
  return formatCallableUserError(error, {
    fallback: 'Failed to load consultations. Please try again.',
    internal: "We couldn't load your consultations. Please try again.",
    deadlineExceeded: 'Loading consultations took too long. Please try again.',
    unavailable: 'Consultations are temporarily unavailable. Please try again.',
    unauthenticated: 'Please sign in again to view consultations.',
  })
}

export function formatConsultationsHubCancelError(error: unknown): string {
  return formatCallableUserError(error, {
    fallback: 'Failed to cancel appointment. Please try again.',
    internal: "We couldn't cancel your appointment. Please try again.",
    deadlineExceeded: 'Cancelling your appointment took too long. Please try again.',
    unavailable: 'Cancellation is temporarily unavailable. Please try again.',
    unauthenticated: 'Please sign in again to manage your appointment.',
  })
}

export function logConsultationsHubFetchFailure(args: {
  customerId: string | null
  phase: ConsultationsHubFetchPhase
  attempt: number
  maxAttempts: number
  durationMs: number
  error: unknown
  superseded?: boolean
}): void {
  const payload = {
    customerId: args.customerId,
    phase: args.phase,
    attempt: args.attempt,
    maxAttempts: args.maxAttempts,
    durationMs: args.durationMs,
    code: getErrorCode(args.error),
    message: getErrorMessage(args.error),
    superseded: args.superseded ?? false,
    error: args.error,
  }
  if (args.attempt === args.maxAttempts) {
    logger.error('[consultations-hub] fetchHub failed', payload)
  } else {
    logger.warn('[consultations-hub] fetchHub failed', payload)
  }
}

export function logConsultationsHubCancelFailure(args: {
  consultationId: string
  durationMs: number
  error: unknown
}): void {
  logger.error('[consultations-hub] cancelClinicalConsultation failed', {
    consultationId: args.consultationId,
    durationMs: args.durationMs,
    code: getErrorCode(args.error),
    message: getErrorMessage(args.error),
    error: args.error,
  })
}

export function delayMs(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}
